import { useEffect, useRef, useState } from 'react';
import {
  motion,
  useMotionValue,
  useSpring,
  useTransform,
} from 'framer-motion';
import {
  HiOutlineArrowDown,
  HiOutlineLocationMarker,
} from 'react-icons/hi';
import { SiDevbox } from "react-icons/si";
import { FiGithub, FiLinkedin, FiMail } from 'react-icons/fi';
import { profile } from '../data/content';
import './Hero.css';

/* Tiny typewriter for the rotating role line. */
function useTypewriter(words, { typeSpeed = 90, deleteSpeed = 40, pause = 1600 } = {}) {
  const [index, setIndex] = useState(0);
  const [text, setText] = useState('');
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const current = words[index % words.length];
    let timeout;

    if (!deleting && text === current) {
      timeout = setTimeout(() => setDeleting(true), pause);
    } else if (deleting && text === '') {
      setDeleting(false);
      setIndex((i) => i + 1);
    } else {
      timeout = setTimeout(
        () => {
          setText((prev) =>
            deleting
              ? current.slice(0, prev.length - 1)
              : current.slice(0, prev.length + 1)
          );
        },
        deleting ? deleteSpeed : typeSpeed
      );
    }
    return () => clearTimeout(timeout);
  }, [text, deleting, index, words, typeSpeed, deleteSpeed, pause]);

  return text;
}

export default function Hero() {
  const typed = useTypewriter(profile.roles);
  const ref = useRef(null);

  // Parallax based on pointer position over the hero.
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const px = useSpring(mx, { stiffness: 60, damping: 18 });
  const py = useSpring(my, { stiffness: 60, damping: 18 });

  const orb1X = useTransform(px, [-0.5, 0.5], [-30, 30]);
  const orb1Y = useTransform(py, [-0.5, 0.5], [-20, 20]);
  const orb2X = useTransform(px, [-0.5, 0.5], [24, -24]);
  const orb2Y = useTransform(py, [-0.5, 0.5], [18, -18]);

  const onMove = (e) => {
    const rect = ref.current.getBoundingClientRect();
    mx.set((e.clientX - rect.left) / rect.width - 0.5);
    my.set((e.clientY - rect.top) / rect.height - 0.5);
  };

  const scrollTo = (id) =>
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });

  return (
    <section id="home" className="hero" ref={ref} onMouseMove={onMove}>
      {/* Floating gradient orbs */}
      <motion.div
        className="hero__orb hero__orb--1"
        style={{ x: orb1X, y: orb1Y }}
        aria-hidden
      />
      <motion.div
        className="hero__orb hero__orb--2"
        style={{ x: orb2X, y: orb2Y }}
        aria-hidden
      />
      <div className="hero__grid" aria-hidden />

      <div className="container hero__inner">

        <motion.h1
          className="hero__title"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.08 }}
        >
          <span className="hero__hi">Hi, I'm</span>
          <span className="hero__name gradient-text">{profile.name}</span>
        </motion.h1>

        <motion.div
          className="hero__role"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.18 }}
        >
          <SiDevbox className="hero__spark" />
          <span className="hero__typed">{typed}</span>
          <span className="hero__caret" />
        </motion.div>

        <motion.p
          className="hero__tagline"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.28 }}
        >
          {profile.tagline}
        </motion.p>

        <motion.div
          className="hero__actions"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.38 }}
        >
          <button
            className="btn btn-primary"
            onClick={() => scrollTo('projects')}
          >
            View my work
            <HiOutlineArrowDown />
          </button>
          <button
            className="btn btn-ghost"
            onClick={() => scrollTo('contact')}
          >
            <FiMail /> Let's talk
          </button>
        </motion.div>

        <motion.div
          className="hero__socials"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.7, delay: 0.5 }}
        >
          <a href={profile.linkedin} target="_blank" rel="noreferrer" aria-label="LinkedIn">
            <FiLinkedin />
          </a>
          <a href={profile.github} target="_blank" rel="noreferrer" aria-label="GitHub">
            <FiGithub />
          </a>
          <a href={`mailto:${profile.email}`} aria-label="Email">
            <FiMail />
          </a>
          <span className="hero__loc">
            <HiOutlineLocationMarker /> {profile.location}
          </span>
        </motion.div>

        <motion.div
          className="hero__stats"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.6 }}
        >
          {profile.stats.map((s) => (
            <div key={s.label} className="hero__stat">
              <span className="hero__stat-value gradient-text">{s.value}</span>
              <span className="hero__stat-label">{s.label}</span>
            </div>
          ))}
        </motion.div>
      </div>

      <motion.button
        className="hero__scroll"
        onClick={() => scrollTo('about')}
        aria-label="Scroll to about"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1 }}
      >
        <span className="hero__mouse">
          <span className="hero__wheel" />
        </span>
      </motion.button>
    </section>
  );
}
